import { Button, Checkbox, Dialog } from '@web_opxp/components';
import { StatsSelectors, useTranslation } from '@web_opxp/features';
import { FC, useState } from 'react';
import { useSelector } from 'react-redux';
import { ColourblindnessCategoryGroupIds, DyscalculiaCategoryGroupIds } from '../base/constants';
import { CyclesSelectors } from '../cycles';
import { IExercisePreference } from './ExerciseSection';
import { useColourblindnessStatValue, useDyscalculiaStatValue } from './specialNeedsHooks';
import { EImpairment } from './types';

export interface ISpecialNeedsAlertProps {
  preference: IExercisePreference;
  onSaveWorkoutStat: (workoutId: number, workoutEnabled: boolean, disableImpairment?: boolean) => void;
}

const ExercisePreferenceCheckbox: FC<ISpecialNeedsAlertProps> = ({ preference, onSaveWorkoutStat }) => {
  const { t } = useTranslation();
  const { exerciseId, categoryGroupId, workoutEnabled: stat, workoutId } = preference;
  const [isAlertVisible, setIsAlertVisible] = useState<boolean>(false);
  const [impairmentDialog, setImpairmentDialog] = useState<EImpairment>();
  const closeAlertForExercise = () => {
    onSaveWorkoutStat(workoutId, true, true);
    setIsAlertVisible(false);
  };
  const colourblindness = useColourblindnessStatValue();
  const dyscalculia = useDyscalculiaStatValue();
  const handlePreferenceChange = (enabled: boolean) => {
    if (enabled) {
      // if user turn exercise on - we must check for impairments
      // if colorblindness is on and exercise belongs to the impairment affected
      if (colourblindness && ColourblindnessCategoryGroupIds.includes(categoryGroupId)) {
        setImpairmentDialog(EImpairment.ColourBlindness);
        setIsAlertVisible(true);
        return;
      }
      // if dyscalculia is on and exercise belongs to the impairment affected
      if (dyscalculia && DyscalculiaCategoryGroupIds.includes(categoryGroupId)) {
        setImpairmentDialog(EImpairment.Dyscalculia);
        setIsAlertVisible(true);
        return;
      }
    }
    onSaveWorkoutStat(workoutId, enabled);
  };
  const cyclesLoading = useSelector(CyclesSelectors.isCyclesLoading);
  const cyclesSaving = useSelector(CyclesSelectors.isCyclesSaving);
  const statsSaving = useSelector(StatsSelectors.isSaving);
  const workInProgress = cyclesLoading || cyclesSaving || statsSaving;

  return (
    <>
      <Checkbox
        className="checkboxContainer col-6"
        id={`${exerciseId}-${workoutId}`}
        key={`${exerciseId}-${workoutId}`}
        onChange={handlePreferenceChange}
        disabled={workInProgress}
        checked={stat}
        data-testid="exercise-preference-checkbox">
        <span>{t(`common.exercises:Title.${preference.exerciseId}`)}</span>
      </Checkbox>
      <Dialog
        autoFocus={true}
        isOpen={isAlertVisible}
        onClose={closeAlertForExercise}
        usePortal={true}
        className="special-needs-alert"
        headerContent={<h3>{t('Personalization.SpecialNeeds.ExerciseAlert.Title')}</h3>}
        footerContent={
          <Button label={t('Personalization.SpecialNeeds.ExerciseAlert.Close')} onClick={closeAlertForExercise} />
        }>
        <p className="special-needs-alert-body">{t('Personalization.SpecialNeeds.ExerciseAlert.Message')}</p>
        <ul>{impairmentDialog ? <li>{t(`Personalization.SpecialNeeds.${impairmentDialog}`)}</li> : null}</ul>
      </Dialog>
    </>
  );
};

export default ExercisePreferenceCheckbox;
